import siteOutlined from "@/assets/img/site_outlined.svg";
import stationOutlined from "@/assets/img/station_outlined.svg";
import { useDrawerState, useDrawerStore } from "@/stores";
import { LibLoader, Marker } from "@soft-db/google-maps";
import { useScreenSize } from "@soft-db/ui";
import { storeToRefs } from "pinia";
import { computed, ref, ShallowRef } from "vue";
import { useRoute } from "vue-router";

export const MapItemType = {
	Station: "station",
	Site: "site",
} as const;
export type MapItemType = (typeof MapItemType)[keyof typeof MapItemType];

declare module "@soft-db/google-maps" {
	interface MarkerMeta {
		id: string;
		type: MapItemType;
		name: string;
		image?: string;
	}
}

export type MarkerPosition = google.maps.LatLng | google.maps.LatLngLiteral;

export function toLiteralPosition(pos: MarkerPosition): google.maps.LatLngLiteral {
	return {
		lat: typeof pos.lat === "number" ? pos.lat : pos.lat(),
		lng: typeof pos.lng === "number" ? pos.lng : pos.lng(),
	};
}

const ZERO_COORDINATE = 1e-6;

export const useMarkerManager = (mapRef: ShallowRef<google.maps.Map | undefined>) => {
	const route = useRoute();

	const mapLoaded = ref(false);

	const siteIcon = ref<google.maps.Icon | null>(null);
	const stationIcon = ref<google.maps.Icon | null>(null);

	LibLoader.loadCore().then(() => {
		siteIcon.value = {
			url: siteOutlined,
			size: new google.maps.Size(36, 36),
			scaledSize: new google.maps.Size(36, 36),
			anchor: new google.maps.Point(18, 36),
		};

		stationIcon.value = {
			url: stationOutlined,
			size: new google.maps.Size(36, 36),
			scaledSize: new google.maps.Size(36, 36),
			anchor: new google.maps.Point(18, 18),
		};
	});
	LibLoader.loadMarker().then(() => (mapLoaded.value = true));

	const state = computed(() => {
		const { id, type, move } = route.query;
		return {
			id: id?.toString(),
			type: type == null ? null : type.toString(),
			move: move ? true : false,
		};
	});

	const { isLoading, fieldFilters, items: drawerItems } = storeToRefs(useDrawerState());

	// Google Map Clusterer don't handle 0 has valid coordinate.
	// For now, we force 1e-6.
	//
	// We should eventually just remove all markers at 0,0 OR try so send a PR the the lib maintainers
	const generateSafeCoordinatePoint = (val: number | null | undefined): number => {
		if (val == null || val === 0) return ZERO_COORDINATE;

		return val;
	};

	const markers = computed<Marker[]>(() => {
		if (!mapLoaded.value || isLoading.value) return [];

		return drawerItems.value.map<Marker>((item) => {
			const object = item;
			const position: google.maps.LatLngLiteral = {
				lat: generateSafeCoordinatePoint(object.coordinates.latitude),
				lng: generateSafeCoordinatePoint(object.coordinates.longitude),
			};

			let icon: google.maps.Icon | null;
			let type: MapItemType;
			if (object.object === "site") {
				icon = siteIcon.value;
				type = "site";
			} else {
				icon = stationIcon.value;
				type = "station";
			}

			return {
				id: `${type}-${object.id}`,
				clickable: true,
				icon,
				position,
				meta: {
					id: object.id,
					type,
					name: object.name,
					image: object.image,
				},
			};
		});
	});

	const { view } = storeToRefs(useDrawerStore());
	const handle = watch(
		[mapRef, markers, isLoading],
		() => {
			if (isLoading.value || mapRef.value == null) {
				return;
			}

			handle.pause();
			if (activeMarker.value) {
				return;
			}

			fitView();
		},
		{ flush: "post" }
	);
	watch(
		[view, fieldFilters],
		() => {
			handle.resume();
		},
		{ flush: "sync" }
	);

	const activeMarker = computed(() => {
		const { id, type } = state.value;
		return (
			markers.value.find(
				(marker) => marker.meta?.id === id?.toString() && marker.meta?.type === type
			) || null
		);
	});

	const { mdOrLower } = useScreenSize();
	function panToMarker(marker: Marker) {
		if (mapRef.value == null) {
			return;
		}

		const xOffset = mdOrLower.value ? 0 : 0.003;
		const yOffset = mdOrLower.value ? 0.002 : 0;

		const literalPos = toLiteralPosition(marker.position);
		const nextPosition = new google.maps.LatLng(literalPos.lat - yOffset, literalPos.lng - xOffset);

		mapRef.value.setCenter(nextPosition);
		mapRef.value.setZoom(16);
	}

	const defaultCenter = { lat: 46.8139, lng: -71.208 };
	const defaultZoom = 5;
	function fitView() {
		if (mapRef.value == null) {
			return;
		}

		if (markers.value.length === 0) {
			mapRef.value.setCenter(defaultCenter);
			mapRef.value.setZoom(defaultZoom);

			return;
		}

		const bounds = new google.maps.LatLngBounds();
		for (const el of markers.value) {
			bounds.extend(el.position);
		}

		const left = mdOrLower.value ? 128 : 350;
		mapRef.value.fitBounds(bounds, { left, bottom: 128, right: 128, top: 128 });
	}

	watch([activeMarker, mapRef], (_, [prevMarker]) => {
		if (mapRef.value == null || activeMarker.value == null) {
			return;
		}

		if (activeMarker.value.id !== prevMarker?.id) {
			panToMarker(activeMarker.value);
		}
	});

	return { state, markers, activeMarker };
};
